'use client'

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ChevronDown, Search, X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import {
  filterPickerOptions,
  selectionSummary,
  selectionValues,
} from './search-filter-helpers'
import styles from './search-filter-picker.module.css'

/**
 * @typedef {{ value: string, label: string, shortLabel?: string, keywords?: string[], group?: string, disabled?: boolean }} SearchFilterOption
 * @param {{ label: string, value: string | null | string[], onChange: (value: string | null | string[]) => void, options?: SearchFilterOption[], multi?: boolean, searchable?: boolean, loading?: boolean, disabled?: boolean, initializing?: boolean }} props
 */
export default function SearchFilterPicker({
  label,
  value,
  onChange,
  options = [],
  multi = false,
  searchable = false,
  loading = false,
  disabled = false,
  initializing = false,
}) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [mobile, setMobile] = useState(false)
  const [ready, setReady] = useState(false)
  const searchRef = useRef(null)
  const headingRef = useRef(null)
  const triggerRef = useRef(null)
  const tabExitRef = useRef(null)
  useEffect(() => {
    const media = window.matchMedia('(max-width: 767px)')
    const update = () => {
      setMobile(media.matches)
      setOpen(false)
      setQuery('')
    }
    setMobile(media.matches)
    setReady(true)
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  const preparing = !ready || initializing
  const changeOpen = (next) => {
    setOpen(next)
    if (!next) setQuery('')
  }
  const values = selectionValues(value)
  const selectedLabels = values.map(
    (entry) => options.find((option) => option.value === entry)?.label || entry,
  )
  const filtered = filterPickerOptions(options, query)
  const groups = [
    ...new Set(filtered.map((option) => option.group || '')),
  ].sort((a, b) => (a === 'Popular' ? -1 : b === 'Popular' ? 1 : 0))
  const choose = (option) => {
    if (option.disabled) return
    onChange(
      multi
        ? values.includes(option.value)
          ? values.filter((entry) => entry !== option.value)
          : [...values, option.value]
        : option.value,
    )
    if (!multi) changeOpen(false)
  }
  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      disabled={preparing || disabled || loading}
      aria-busy={preparing || loading}
      aria-label={`${label}: ${selectedLabels.join(', ') || 'Any'}`}
      className={`flex min-h-14 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-neutral-blue-500 bg-white px-3 text-left font-semibold text-neutral-blue-900 hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 ${preparing ? 'disabled:cursor-wait disabled:opacity-100' : 'disabled:cursor-not-allowed disabled:opacity-50'}`}
    >
      <span className="truncate" title={selectedLabels.join(', ') || 'Any'}>
        {loading ? 'Loading…' : selectionSummary(options, value)}
      </span>
      <ChevronDown
        aria-hidden="true"
        className={`size-4 shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
      />
    </button>
  )
  const content = (
    <div
      className="flex min-h-0 flex-col text-neutral-blue-900"
      onKeyDown={(event) => {
        if (!mobile && event.key === 'Tab') {
          const inputs = [
            ...event.currentTarget.querySelectorAll(
              'input:not([disabled]), button:not([disabled])',
            ),
          ]
          const stops = inputs.filter(
            (input) =>
              input.type !== 'radio' ||
              input.checked ||
              (!inputs.some(
                (other) => other.type === 'radio' && other.checked,
              ) &&
                input === inputs.find((other) => other.type === 'radio')),
          )
          const backward =
            event.shiftKey &&
            (event.target === stops[0] || event.target === headingRef.current)
          const forward =
            !event.shiftKey &&
            (event.target === stops.at(-1) || stops.length === 0)
          if (backward || forward) {
            event.preventDefault()
            event.stopPropagation()
            const trigger = triggerRef.current
            const candidates = [
              ...document.querySelectorAll(
                'button, a[href], input, select, textarea, [tabindex]',
              ),
            ].filter(
              (element) =>
                element.tabIndex >= 0 &&
                !element.disabled &&
                element.getClientRects().length > 0 &&
                !element.closest('[inert], [hidden]'),
            )
            tabExitRef.current = backward
              ? trigger
              : candidates[candidates.indexOf(trigger) + 1] || trigger
            changeOpen(false)
          }
        }
        if (event.key === 'Enter') {
          event.stopPropagation()
          if (event.target.tagName === 'INPUT') event.preventDefault()
        }
      }}
    >
      <div
        className={
          mobile
            ? 'shrink-0 border-b border-slate-200 px-4 py-3'
            : searchable
              ? 'shrink-0 border-b border-slate-200 p-2'
              : 'sr-only'
        }
      >
        {mobile ? (
          <DrawerTitle
            ref={headingRef}
            tabIndex={-1}
            className="font-semibold outline-none"
          >
            {label}
          </DrawerTitle>
        ) : (
          <h3
            ref={headingRef}
            tabIndex={-1}
            id={`${id}-title`}
            className="sr-only outline-none"
          >
            {label}
          </h3>
        )}
        {mobile && (
          <DrawerDescription className="sr-only">
            Choose {multi ? 'one or more options' : 'one option'}. Search
            applies your choices.
          </DrawerDescription>
        )}
        {mobile && (
          <p aria-live="polite" className="mt-1 text-sm text-slate-600">
            {values.length
              ? `${values.length} selected`
              : 'Any (no restriction)'}
          </p>
        )}
        {searchable && (
          <div
            className={`${mobile ? 'mt-3' : ''} flex items-center gap-2 rounded-md border border-slate-400 px-2 focus-within:border-transparent focus-within:outline-2 focus-within:outline-offset-[-1px] focus-within:outline-orange-600`}
          >
            <Search className="size-4 shrink-0" aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              size={1}
              aria-label={`Search ${label.toLowerCase()}`}
              placeholder={`Search ${label.toLowerCase()}…`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="min-h-11 min-w-0 flex-1 bg-transparent outline-none [&::-webkit-search-cancel-button]:appearance-none"
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search text"
                onClick={() => setQuery('')}
                className="p-2"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        )}
      </div>
      <div
        className={`${styles.options} min-h-0 overflow-y-auto overscroll-contain py-2`}
        style={{ maxHeight: mobile ? '55dvh' : 'min(360px, 45vh)' }}
      >
        <fieldset className="min-w-0">
          <legend className="sr-only">{label}</legend>
          {loading ? (
            <p role="status" className="p-3">
              Loading options…
            </p>
          ) : !filtered.length ? (
            <p role="status" className="p-3">
              {query
                ? `No matches for “${query}”. Try another search.`
                : 'No options available.'}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group}>
                {group && (
                  <p className="px-5 pb-1 pt-3 text-xs font-semibold text-slate-600">
                    {group}
                  </p>
                )}
                {filtered
                  .filter((option) => (option.group || '') === group)
                  .map((option) => (
                    <label
                      key={option.value}
                      className={`flex ${mobile ? 'min-h-11' : 'min-h-9'} items-center gap-3 px-5 py-2 text-sm ${option.disabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-neutral-blue-900 hover:bg-slate-100'}`}
                    >
                      <input
                        type={multi ? 'checkbox' : 'radio'}
                        name={id}
                        value={option.value}
                        checked={values.includes(option.value)}
                        disabled={option.disabled}
                        onChange={() => choose(option)}
                        onClick={() => {
                          if (
                            !option.disabled &&
                            !multi &&
                            values.includes(option.value)
                          )
                            changeOpen(false)
                        }}
                        className="size-4 shrink-0 accent-orange-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-blue-900"
                      />
                      <span className="min-w-0 [overflow-wrap:anywhere]">
                        {option.label}
                      </span>
                    </label>
                  ))}
              </div>
            ))
          )}
        </fieldset>
      </div>
      <div
        className={
          mobile
            ? 'flex shrink-0 items-center justify-between border-t border-slate-200 bg-white px-4 py-3 pb-[max(12px,env(safe-area-inset-bottom))]'
            : 'flex shrink-0 border-t border-slate-200 bg-white px-2 py-1'
        }
      >
        <button
          type="button"
          disabled={!values.length}
          onClick={() => onChange(multi ? [] : null)}
          className={`${mobile ? 'min-h-11' : 'min-h-8'} cursor-pointer rounded-md px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-blue-900 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent`}
        >
          Clear
        </button>
        {mobile && (
          <button
            type="button"
            onClick={() => changeOpen(false)}
            className="min-h-11 rounded-md bg-orange-600 px-5 font-semibold text-white hover:bg-orange-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
          >
            Done
          </button>
        )}
      </div>
    </div>
  )
  return (
    <div className="min-w-0">
      <p
        id={`${id}-label`}
        className="mb-2 text-sm font-semibold text-neutral-blue-200"
      >
        {label}
      </p>
      {mobile ? (
        <Drawer
          open={open}
          onOpenChange={changeOpen}
          shouldScaleBackground={false}
        >
          <DrawerTrigger asChild>{trigger}</DrawerTrigger>
          <DrawerContent
            className="max-h-[90dvh] border-slate-200 bg-white"
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              headingRef.current?.focus({ preventScroll: true })
            }}
          >
            {open && content}
          </DrawerContent>
        </Drawer>
      ) : (
        <Popover open={open} onOpenChange={changeOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent
            onCloseAutoFocus={(event) => {
              if (tabExitRef.current) {
                event.preventDefault()
                tabExitRef.current.focus()
                tabExitRef.current = null
              }
            }}
            align="start"
            aria-labelledby={`${id}-title`}
            collisionPadding={12}
            style={{
              width: 'max-content',
              minWidth: 'var(--radix-popover-trigger-width)',
              maxWidth: 'min(36rem, calc(100vw - 24px))',
            }}
            className="flex max-h-[var(--radix-popover-content-available-height)] max-w-[calc(100vw-24px)] flex-col overflow-hidden border-slate-200 bg-white p-0 shadow-xl"
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              ;(searchRef.current || headingRef.current)?.focus({
                preventScroll: true,
              })
            }}
          >
            {open && (
              <>
                {/* Preserve the full list's intrinsic width while searching. */}
                <div
                  aria-hidden="true"
                  className="invisible h-0 shrink-0 overflow-hidden text-sm"
                >
                  {options.map((option) => (
                    <div key={option.value} className="flex gap-3 px-5">
                      <span className="w-4 shrink-0" />
                      <span className="whitespace-nowrap">{option.label}</span>
                    </div>
                  ))}
                </div>
                {content}
              </>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
